import { z } from 'zod';

/**
 * What the API serialises — the migration runner's contract, relayed — parsed
 * rather than cast. Unknown keys are stripped; the enums are closed, because a
 * state this screen does not know is one it cannot tell finished from running.
 *
 * Source of truth: `openspec/changes/bulk-salesforce-migration/design.md`,
 * "Runner HTTP contract".
 */

export const JOB_STATES = ['queued', 'planning', 'running', 'done', 'failed'] as const;
export const JobState = z.enum(JOB_STATES);
export type JobState = z.infer<typeof JobState>;

export const JobMode = z.enum(['dry-run', 'launch', 'report']);
export type JobMode = z.infer<typeof JobMode>;

export const TENANT_OUTCOMES = [
    'pending',
    'planned',
    'not_migrated',
    'launched',
    'blocked',
    'running',
    'completed',
    'completed_with_warnings',
    'failed',
    'skipped',
    'error',
] as const;
export const TenantOutcome = z.enum(TENANT_OUTCOMES);
export type TenantOutcome = z.infer<typeof TenantOutcome>;

export const ColumnDef = z.object({
    key: z.string(),
    label: z.string(),
    align: z.enum(['left', 'center', 'right']).optional(),
});
export type ColumnDef = z.infer<typeof ColumnDef>;

export const BulkModule = z.object({
    moduleId: z.string(),
    title: z.string(),
    description: z.string(),
    domain: z.object({ id: z.string(), title: z.string() }),
    order: z.number(),
    columns: z.array(ColumnDef),
    supportsPreview: z.boolean(),
});
export type BulkModule = z.infer<typeof BulkModule>;

export const JobHeader = z.object({
    moduleId: z.string(),
    reportId: z.string(),
    mode: JobMode,
    state: JobState,
    requestedBy: z.string(),
    reason: z.string().nullable(),
    fromReportId: z.string().nullable(),
    tenantIds: z.array(z.string()).nullable(),
    excludeSandboxes: z.boolean(),
    total: z.number().nullable(),
    done: z.number(),
    counts: z.record(z.number()),
    createdAt: z.string(),
    startedAt: z.string().nullable(),
    finishedAt: z.string().nullable(),
    lastProgressAt: z.string().nullable(),
    error: z.string().nullable(),
    exportLocation: z.string().nullable(),
});
export type JobHeader = z.infer<typeof JobHeader>;

export const RecordRow = z.object({
    sourceId: z.string(),
    title: z.string(),
    columns: z.record(z.unknown()),
    notMigrated: z.object({ code: z.string(), message: z.string() }).nullable(),
    preview: z.unknown().optional(),
    tracking: z
        .object({
            status: z.string(),
            attempts: z.number(),
            v2Id: z.string().nullable(),
            failure: z.unknown(),
            warnings: z.array(z.unknown()),
            migratedAt: z.string().nullable(),
        })
        .optional(),
});
export type RecordRow = z.infer<typeof RecordRow>;

export const RunView = z.object({
    start: z.enum(['accepted', 'blocked', 'already-running']),
    runId: z.string().nullable(),
    status: z.string().optional(),
    totals: z.record(z.number()).optional(),
    blockedBy: z.unknown().optional(),
    abortedBy: z.unknown().optional(),
    startedAt: z.string().optional(),
    finishedAt: z.string().optional(),
});
export type RunView = z.infer<typeof RunView>;

export const TenantRow = z.object({
    tenant: z.object({
        tenantId: z.string(),
        slug: z.string().nullable(),
        displayName: z.string(),
        orgId: z.string().nullable(),
        isSandbox: z.boolean(),
        status: z.string(),
        salesforceSystemUser: z.boolean(),
    }),
    outcome: TenantOutcome,
    reason: z.string().nullable(),
    message: z.string().nullable(),
    records: z.array(RecordRow),
    recordsTotal: z.number(),
    run: RunView.optional(),
    completedAt: z.string().nullable(),
});
export type TenantRow = z.infer<typeof TenantRow>;

export const ModulesResponse = z.object({ modules: z.array(BulkModule) });
export const JobResponse = z.object({ job: JobHeader });
export const JobsResponse = z.object({ jobs: z.array(JobHeader) });
export const JobPage = z.object({
    job: JobHeader,
    rows: z.array(TenantRow),
    nextCursor: z.string().nullable(),
});
export type JobPage = z.infer<typeof JobPage>;
